import * as THREE from "three";
import { BaseModify } from "../base/BaseModify";
import { Context } from "../core/context";
import { DBEntity, DbLine, FMathUtils, generateUUID } from "@/CadEngine";
import {
  ModifyOperateState,
  DrawOperationMovePointBeforeParams,
  DrawOperationMovePointParams,
  IModifyOperation,
} from "./IModifyOperation";
import { ULabel } from "@/ui/ULabel";
import { CommonColor, Linearity } from "@/core/Parameters";
import { WasmUtil } from "@/utils/WasmUtil";

class ClippingModify implements IModifyOperation {
  private m_context: Context;
  private m_selects: DBEntity[] | undefined;
  private m_label: ULabel;
  private m_curPoint: THREE.Vector3 = new THREE.Vector3();
  private m_curSelect: DbLine | undefined;
  private m_tempLine: THREE.Line | undefined;
  private m_crossPoints: THREE.Vector3[] = [];
  private m_createLinesPoints: THREE.Vector3[] = [];
  private m_prevSelects: DbLine[] = [];
  private m_addLines: DbLine[][] = [];
  private m_originLines: DbLine[] = [];
  private m_locationObject: THREE.Line;

  constructor(context: Context) {
    this.m_context = context;
    this.m_label = this.m_context.ModifyManager.TextLabel;
    this.m_selects = this.m_context.SelectionManager.GetSelected() as DbLine[];
    this.m_context.SelectionManager.CancelPick();
    this.m_context.SelectionManager.HighLighting = false;
    this.m_originLines =
      this.m_context.SelectionManager.GetSelected() as DbLine[];
    this.m_locationObject = context.ToolsManager.CreatedLocationLine();
    this.createTempLine();
  }

  OnMovePointBefore(params: DrawOperationMovePointBeforeParams): void {}
  OnMovePoint(point: DrawOperationMovePointParams): void {
    this.m_curSelect = undefined;
    this.setSelect();
    this.m_curPoint = point.point.clone();
    this.m_locationObject.position.copy(this.m_curPoint);
    if (point.obejct?.length && this.m_selects?.length) {
      this.m_curSelect = point.obejct[0] as DbLine;
      let res = this.filterCurSelect();
      if (!res) return;
      this.computationCrossPoint();
      if (this.m_crossPoints.length) {
        this.setTempLinePoints();
      }
    }
  }
  OnDownPoint(point: DrawOperationMovePointParams): void {
    this.setSelect();
    let res = this.filterCurSelect();
    if (!res) return;
    if (point.obejct?.length) {
      if (this.m_crossPoints.length) {
        this.createClippingFinishLines();
      }
    }
  }
  onKeyDown(e: KeyboardEvent): void {}
  onKeyUp(e: KeyboardEvent): void {}

  OnZoom(camera: THREE.Camera): void {}
  OnWheel(e: WheelEvent): void {}

  onMouseMove(e: PointerEvent): void {}
  onMouseDown(e: PointerEvent): void {}
  onMouseUp(e: PointerEvent): void {
    this.setSelect();
    this.filterCurSelect();
  }

  Finish(operate: ModifyOperateState) {
    this.m_context.SelectionManager.HighLighting = true;
    this.clear();
    let { delData, addData } = this.getResultData();
    if (operate == ModifyOperateState.complete) {
      return { addData, delData };
    } else if (operate == ModifyOperateState.cancel) {
      return undefined;
    }
  }

  private setSelect() {
    if (this.m_selects && this.m_selects.length) {
      this.m_context.SelectionManager.ClearSelected();
      this.m_context.SelectionManager.SetSelected(...this.m_selects);
      this.m_tempLine?.geometry.setFromPoints([]);
    }
  }

  private filterCurSelect() {
    if (this.m_selects && this.m_selects.length && this.m_curSelect) {
      if (this.m_selects.includes(this.m_curSelect)) {
        this.m_context.SelectionManager.RemoveSelect(this.m_curSelect);
        return true;
      } else {
        return false;
      }
    }
  }

  private computationCrossPoint() {
    this.m_crossPoints.length = 0;
    let surplusSelects =
      this.m_context.SelectionManager.GetSelected() as DbLine[];
    if (this.m_curSelect) {
      let curSelectStartPoint = new THREE.Vector3(
        this.m_curSelect?.points[0],
        this.m_curSelect?.points[1],
        this.m_curSelect?.points[2]
      );
      let curSelectEndPoint = new THREE.Vector3(
        this.m_curSelect?.points.at(-3),
        this.m_curSelect?.points.at(-2),
        this.m_curSelect?.points.at(-1)
      );
      if (surplusSelects.length) {
        let crossPoints = [];
        for (let i = 0; i < surplusSelects.length; i++) {
          let crossPoint = WasmUtil.IntersectPoints(
            this.m_context,
            this.m_curSelect,
            surplusSelects[i],
            this.m_context.SelectionManager.Scale
          );
          if (crossPoint && crossPoint.length) {
            crossPoints.push(...crossPoint);
          }
        }
        if (crossPoints.length) {
          this.m_crossPoints.push(...crossPoints);
        } else {
          this.m_crossPoints.push(curSelectStartPoint, curSelectEndPoint);
        }
      } else {
        this.m_crossPoints.push(curSelectStartPoint, curSelectEndPoint);
      }
    }
  }
  private setTempLinePoints() {
    console.log("setTempLinePoints", this.m_crossPoints);
    let curSelectStartPoint = new THREE.Vector3(
      this.m_curSelect?.points[0],
      this.m_curSelect?.points[1],
      this.m_curSelect?.points[2]
    );
    let curSelectEndPoint = new THREE.Vector3(
      this.m_curSelect?.points.at(-3),
      this.m_curSelect?.points.at(-2),
      this.m_curSelect?.points.at(-1)
    );
    let curSelectStartVector = new THREE.Vector3().subVectors(
      curSelectStartPoint,
      this.m_curPoint
    );
    let curSelectEndVector = new THREE.Vector3().subVectors(
      curSelectEndPoint,
      this.m_curPoint
    );
    let curSelectStartVectorRelevantPoints: {
      crossPoint: THREE.Vector3;
      distance: number;
    }[] = [];
    let curSelectEndVectorRelevantPoints: {
      crossPoint: THREE.Vector3;
      distance: number;
    }[] = [];

    for (let i = 0; i < this.m_crossPoints.length; i++) {
      if (this.m_crossPoints[i].distanceTo(curSelectStartPoint) < 0.01) {
        this.m_crossPoints.splice(i, 1);
        i--;
      } else if (this.m_crossPoints[i].distanceTo(curSelectEndPoint) < 0.01) {
        this.m_crossPoints.splice(i, 1);
        i--;
      }
    }
    this.m_crossPoints.push(curSelectStartPoint, curSelectEndPoint);
    for (let i = 0; i < this.m_crossPoints.length; i++) {
      let crossPoint = this.m_crossPoints[i];
      let vector = new THREE.Vector3().subVectors(crossPoint, this.m_curPoint);
      if (curSelectStartVector.dot(vector) >= 0) {
        curSelectStartVectorRelevantPoints.push({
          crossPoint,
          distance: vector.length(),
        });
      }
      if (curSelectEndVector.dot(vector) >= 0) {
        curSelectEndVectorRelevantPoints.push({
          crossPoint,
          distance: vector.length(),
        });
      }
    }
    if (curSelectStartVectorRelevantPoints.length) {
      curSelectStartVectorRelevantPoints.sort((a, b) => {
        return a.distance - b.distance;
      });
    }
    if (curSelectEndVectorRelevantPoints.length) {
      curSelectEndVectorRelevantPoints.sort((a, b) => {
        return a.distance - b.distance;
      });
    }
    let positions = [
      curSelectStartVectorRelevantPoints[0].crossPoint,
      curSelectEndVectorRelevantPoints[0].crossPoint,
    ];
    this.m_tempLine?.geometry.setFromPoints(positions);
    this.m_createLinesPoints = [];
    let InCludeType = "none";
    if (
      positions[0].distanceTo(curSelectStartPoint) < 0.01 &&
      positions[1].distanceTo(curSelectEndPoint) < 0.01
    ) {
      InCludeType = "all";
    } else if (positions[0].distanceTo(curSelectStartPoint) < 0.01) {
      InCludeType = "start";
    } else if (positions[1].distanceTo(curSelectEndPoint) < 0.01) {
      InCludeType = "end";
    }
    if (InCludeType == "none") {
      this.m_createLinesPoints.push(
        curSelectStartPoint,
        curSelectStartVectorRelevantPoints[0].crossPoint,
        curSelectEndVectorRelevantPoints[0].crossPoint,
        curSelectEndPoint
      );
    } else if (InCludeType == "start") {
      this.m_createLinesPoints.push(
        curSelectEndVectorRelevantPoints[0].crossPoint,
        curSelectEndPoint
      );
    } else if (InCludeType == "end") {
      this.m_createLinesPoints.push(
        curSelectStartPoint,
        curSelectStartVectorRelevantPoints[0].crossPoint
      );
    }
  }

  private clear() {
    this.m_context.ToolsManager.ObjectsClear([
      this.m_locationObject,
      this.m_tempLine!,
    ]);
  }

  private createTempLine() {
    let geometry = new THREE.BufferGeometry();
    this.m_tempLine = new THREE.LineSegments(
      geometry,
      new THREE.LineBasicMaterial({
        color: CommonColor.Gray,
      })
    );
    this.m_tempLine.frustumCulled = false;
    this.m_tempLine.position.z = 10;
    this.m_context.SceneManager.AuxModel.add(this.m_tempLine);
  }

  private createClippingFinishLines() {
    let addLines = [];
    if (!this.m_curSelect) return;
    let layer = this.m_context.ModelManager.GetLayerByIdColor(
      this.m_curSelect.layerId,
      this.m_curSelect.color!
    );
    if (!layer) return;
    let selectLayerLineTypeName =
      this.m_context.ToolsManager.GetLayerLineTypeName(this.m_curSelect);
    for (let i = 0; i < this.m_createLinesPoints.length; i += 2) {
      let linePoints = [
        this.m_createLinesPoints[i],
        this.m_createLinesPoints[i + 1],
      ];
      let addLine;
      if (selectLayerLineTypeName == Linearity.Dash) {
        let dashPoints =
          this.m_context.ToolsManager.PassStartPointAndEndPointGetSelectPoint(
            this.m_curSelect,
            linePoints[0],
            linePoints[1]
          );
        dashPoints[0] = linePoints[0].clone();
        dashPoints[dashPoints.length - 1] = linePoints[1].clone();
        addLine = this.m_context.ModelManager.AddCustomLines(
          layer.Name!,
          this.m_curSelect!.color!,
          dashPoints,
          false
        )[0];
      } else {
        addLine = this.m_context.ModelManager.AddCustomLines(
          layer.Name!,
          this.m_curSelect!.color!,
          linePoints
        )[0];
      }

      if (addLine) {
        if (this.m_curSelect?.userData) {
          addLine.userData = this.m_curSelect?.userData;
        } else {
          addLine.userData = this.m_curSelect;
        }
        addLines.push(addLine);
      }
    }

    for (let i = 0; i < this.m_selects!.length; i++) {
      const select = this.m_selects![i];
      if (select == this.m_curSelect) {
        this.m_selects?.splice(i, 1);
        break;
      }
    }
    this.m_selects!.push(...addLines);
    this.m_addLines.push([...addLines]);

    this.m_context.ModelManager.DeleteEntity(this.m_curSelect!);
    this.m_prevSelects.push(this.m_curSelect!);
    this.m_tempLine?.geometry.setFromPoints([]);
  }

  private getResultData() {
    let delData: DbLine[] = [];
    let addData: DbLine[] = [];
    if (!this.m_selects) return { delData, addData };

    for (let i = 0; i < this.m_originLines.length; i++) {
      let originLine = this.m_originLines[i];
      if (!this.m_selects.includes(originLine)) {
        delData.push(originLine);
      }
    }
    for (let i = 0; i < this.m_selects.length; i++) {
      let select = this.m_selects[i] as DbLine;
      if (!this.m_originLines.includes(select)) {
        addData.push(select);
      }
    }
    return { delData, addData };
  }
}

export { ClippingModify };
